import React from 'react';
import {
  Area,
  Box,
  PostHeader,
  Separator,
  Text,
  useTheme,
} from '@ublend-npm/aulaui-core';

import { AulaState } from '@core/types/state';
import { SearchResult } from '@core/actions/omniSearch/types';
import push from '../../../utils/routing';
import { OmniSearchResultPreview } from './OmniSearchResultPreview';
import { useSelector } from 'react-redux';
import { getCurrentSpaceId, getInitialSpaceId } from '@core/selectors/space';

type OmniSearchResultProps = {
  result: SearchResult;
  footerMap: Record<string, string>;
};

export const OmniSearchResult = ({
  result,
  footerMap,
}: OmniSearchResultProps) => {
  const theme = useTheme();
  const spaceId = useSelector(
    (state: AulaState) => getCurrentSpaceId(state) || getInitialSpaceId(state)
  );

  const handleClick = () => {
    const { redirectUrl, redirectQuery } = result;
    if (redirectUrl) {
      if (redirectUrl.includes('materials')) {
        push({
          pathname: redirectUrl.includes('/journey/materials')
            ? redirectUrl
            : redirectUrl.replace('/materials', '/journey/materials'),
          query: {
            from: 'search',
          },
        });
      } else {
        push(redirectUrl);
      }
    } else if (
      redirectQuery &&
      (redirectQuery.inbox || redirectQuery.messageId)
    ) {
      push({
        pathname: `dashboard/${spaceId}/messages/${redirectQuery.inbox}`,
        query: {
          messageId: redirectQuery.messageId,
          inbox: redirectQuery.inbox,
          from: 'search',
        },
      });
    } else {
      push({
        pathname: `dashboard/${spaceId}/community/announcements`,
        query: redirectQuery,
      });
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  };

  const footerValue = footerMap[result.footerKey];
  const keysHighlightMap = result.keysHighlightMap || {};
  const content = JSON.parse(result.content);

  return (
    <Box key={result.id} sx={{ paddingBottom: 2 }}>
      <Area
        transparent
        rounded
        padding="xsmall"
        sx={{ cursor: 'pointer' }}
        onClick={handleClick}
        onKeyPress={handleKeyPress}
        role="button"
        tabIndex={0}
      >
        <PostHeader
          title={result.title}
          date={new Date(result.createdAt)}
          avatarSrc={result.avatar}
        />
        <Separator size="thin" sx={{ marginY: 1 }} />
        <OmniSearchResultPreview
          content={content}
          keysHighlightMap={keysHighlightMap}
        />
        {footerValue ? (
          <>
            <Separator size="thin" sx={{ marginY: 1 }} />
            <Text kind="p3" sx={{ color: theme.palette.text.secondary }}>
              {result.footerPrefix} {footerValue}
            </Text>
          </>
        ) : null}
      </Area>
    </Box>
  );
};

OmniSearchResult.displayName = 'OmniSearchResult';
